import { useEffect, useRef, useState } from 'react';
import { ArrowLeft, Check, ChevronDown, ChevronUp, Image, Layers3, Maximize2, PanelBottom, Redo2, SlidersHorizontal, Sparkles, Undo2, X } from 'lucide-react';
import { useAutoHideScroll } from '../../components/useAutoHideScroll';
import { useModalFocus } from '../../components/useModalFocus';
import { Button } from '../../components/Button';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import type { TattooDesign } from '../../config/designs';
import { CanvasEngine } from '../../editor/CanvasEngine';
import { ImagePrepareError, imageUploadErrorKey, MAX_UPLOAD_MEGABYTES, type PhotoSourceReadDiagnostic } from '../../editor/image';
import type { EditorSnapshot, EditorState } from '../../editor/types';
import { telegram } from '../../telegram/bridge';
import { DesignLibrary, type CustomDesign } from '../design/DesignLibrary';
import { AdjustPanel, LayersPanel, ProPanel, TattooPanel } from './EditorPanels';
import { useI18n } from '../../i18n/I18nProvider';
import { asDesignAssetError, invalidateCatalogAsset, isDesignAssetCancellation } from '../../api/assets';
import { LockedFeature, useCapability } from '../capabilities/Capability';
import { deleteLocalPhoto, loadLocalPhoto, prepareAndStoreLocalPhoto, type LocalPhotoReference } from '../../storage/photos';
import { logPhotoFlow } from '../../diagnostics/photoFlow';

export type EditorTab = 'tattoo' | 'adjust' | 'layers' | 'pro';

interface EditorScreenProps {
  photoId: string;
  initialDesign: TattooDesign;
  initialState?: EditorState | null;
  designs?: TattooDesign[];
  onBack: () => void;
  onResult: (engine: CanvasEngine, design: TattooDesign) => void;
  onReady: (engine: CanvasEngine) => void;
  onDispose?: (engine: CanvasEngine) => void;
  onPrepareError: (error: unknown, photo: File | null) => void;
  onPhotoReadRetrySuccess?: (diagnostic: PhotoSourceReadDiagnostic) => void;
  onPhotoChange: (photo: LocalPhotoReference) => void;
  onDesignPrepareError?: (error: unknown, designId: string) => void;
  onDesignChange?: (design: TattooDesign, custom: boolean) => void;
}

export function EditorScreen({ photoId, initialDesign, initialState, designs, onBack, onResult, onReady, onDispose, onPrepareError, onPhotoReadRetrySuccess, onPhotoChange, onDesignPrepareError, onDesignChange }: EditorScreenProps) {
  const { t } = useI18n();
  const advanced = useCapability('editor.advanced');
  const tRef = useRef(t);
  tRef.current = t;
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const photoInput = useRef<HTMLInputElement>(null);
  const engineRef = useRef<CanvasEngine | null>(null);
  const setup = useRef({ photoId, initialDesign, initialState });
  const photoIdRef = useRef(photoId);
  const onReadyRef = useRef(onReady);
  const onDisposeRef = useRef(onDispose);
  const onPrepareErrorRef = useRef(onPrepareError);
  const onDesignPrepareErrorRef = useRef(onDesignPrepareError);
  const onDesignChangeRef = useRef(onDesignChange);
  const designCatalogRef = useRef(new Map<string, TattooDesign>([[initialDesign.id, initialDesign], ...(designs || []).map((design) => [design.id, design] as const)]));
  onReadyRef.current = onReady;
  onDisposeRef.current = onDispose;
  onPrepareErrorRef.current = onPrepareError;
  onDesignPrepareErrorRef.current = onDesignPrepareError;
  onDesignChangeRef.current = onDesignChange;
  for (const design of designs || []) designCatalogRef.current.set(design.id, design);
  const [snapshot, setSnapshot] = useState<EditorSnapshot | null>(null);
  const [tab, setTab] = useState<EditorTab>('adjust');
  const [picker, setPicker] = useState<'add' | 'replace' | null>(null);
  const pickerRef = useRef<HTMLDivElement>(null);
  const pickerScroll = useAutoHideScroll();
  useModalFocus(Boolean(picker), pickerRef, () => setPicker(null));
  const [sheetExpanded, setSheetExpanded] = useState(false);
  const [focusMode, setFocusMode] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [lockedNotice, setLockedNotice] = useState(false);
  const [pendingPhoto, setPendingPhoto] = useState<File | null>(null);
  const [photoRestoreError, setPhotoRestoreError] = useState(false);
  const [restoreAttempt, setRestoreAttempt] = useState(0);
  const uiOperation = useRef(0);

  useEffect(() => {
    if (!canvasRef.current) return;
    let mounted = true;
    let engine: CanvasEngine;
    setLoading(true);
    setPhotoRestoreError(false);
    logPhotoFlow('EDITOR_IMAGE_RESTORE_START', { photoId: photoIdRef.current, attempt: restoreAttempt });
    try {
      engine = new CanvasEngine(canvasRef.current, {
        onChange: (value) => { if (mounted) setSnapshot(value); },
        onInteractionActive: (isActive) => telegram.lockEditorGestures(isActive),
        onMaskStrokeStart: () => telegram.haptic('light'),
        onError: setError,
        onPhotoDiagnostic: (diagnostic) => console.debug('[WALLD photo]', { ...diagnostic, platform: telegram.platform() }),
        ...(import.meta.env.DEV ? {
          onAdjustmentDiagnostic: (diagnostic: import('../../editor/types').EditorAdjustmentDiagnostic) => console.debug('[WALLD editor adjustment]', diagnostic),
        } : {}),
      });
    } catch (cause) {
      const error = new ImagePrepareError('canvas_init_failed', 'canvas_init', undefined, { cause });
      logPhotoFlow('EDITOR_IMAGE_RESTORE_FAIL', { photoId: photoIdRef.current, stage: 'canvas_init', errorClass: error.name });
      setLoading(false);
      setPhotoRestoreError(true);
      onPrepareErrorRef.current(error, null);
      return;
    }
    engineRef.current = engine;
    void (async () => {
      let photoInitialized = false;
      let restoredPhoto: File | null = null;
      try {
        try {
          restoredPhoto = await loadLocalPhoto(photoIdRef.current);
          await engine.loadPhoto(restoredPhoto, photoIdRef.current);
          logPhotoFlow('EDITOR_IMAGE_RESTORE_SUCCESS', { photoId: photoIdRef.current, mime: restoredPhoto.type, bytes: restoredPhoto.size });
        }
        catch (cause) {
          if (mounted && (!(cause instanceof Error) || cause.name !== 'AbortError')) {
            const error = cause instanceof ImagePrepareError ? cause : new ImagePrepareError('unknown_editor_prepare_error', 'photo_load', undefined, { cause });
            logPhotoFlow('EDITOR_IMAGE_RESTORE_FAIL', { photoId: photoIdRef.current, stage: error.stage, errorClass: error.name });
            setLoading(false);
            setPhotoRestoreError(true);
            onPrepareErrorRef.current(error, restoredPhoto);
          }
          return;
        }
        photoInitialized = true;
        if (setup.current.initialState) await engine.restoreState(setup.current.initialState);
        else await engine.addLayer({
          designId: setup.current.initialDesign.id,
          name: setup.current.initialDesign.name,
          sourceUrl: setup.current.initialDesign.asset,
          opacity: setup.current.initialDesign.recommendedOpacity,
          blendMode: setup.current.initialDesign.defaultBlend,
          whiteThreshold: setup.current.initialDesign.whiteRemovalThreshold,
        });
        if (mounted) onReadyRef.current(engine);
      } catch (cause) {
        const classified = asDesignAssetError(cause);
        const assetError = classified.code === 'design_unknown_error' ? asDesignAssetError(cause, 'editor_design_add_failed', 'unknown') : classified;
        if (mounted && !isDesignAssetCancellation(assetError)) {
          invalidateCatalogAsset(setup.current.initialDesign.asset);
          onDesignPrepareErrorRef.current?.(assetError, setup.current.initialDesign.id);
          setError(tRef.current('design.loadError'));
        }
      } finally { if (mounted && photoInitialized) setLoading(false); }
    })();
    return () => {
      mounted = false;
      uiOperation.current += 1;
      engineRef.current = null;
      telegram.lockEditorGestures(false);
      engine.destroy();
      onDisposeRef.current?.(engine);
    };
  }, [restoreAttempt]);

  const engine = engineRef.current;
  const chooseDesign = async (design: TattooDesign, custom = false) => {
    if (!engine || !picker) return;
    const operation = ++uiOperation.current;
    setError(''); setLoading(true);
    try {
      const input = { designId: design.id, name: design.name, sourceUrl: design.asset, isCustom: custom, opacity: design.recommendedOpacity, blendMode: design.defaultBlend };
      if (picker === 'replace') await engine.replaceSelected(input); else await engine.addLayer(input);
      if (operation !== uiOperation.current) return;
      designCatalogRef.current.set(design.id, design);
      setPicker(null);
      setTab('adjust');
      telegram.haptic('success');
      onDesignChangeRef.current?.(design, custom);
    } catch (cause) {
      const assetError = asDesignAssetError(cause);
      if (operation === uiOperation.current && !isDesignAssetCancellation(assetError)) {
        invalidateCatalogAsset(design.asset);
        onDesignPrepareErrorRef.current?.(assetError, design.id);
        setError(t('editor.addError'));
      }
      throw assetError;
    }
    finally { if (operation === uiOperation.current) setLoading(false); }
  };
  const chooseCustom = (custom: CustomDesign): Promise<void> => chooseDesign(custom.design, true);

  const applyPhoto = async () => {
    const file = pendingPhoto;
    if (!file || !engine) return;
    const operation = ++uiOperation.current;
    let stored: LocalPhotoReference | null = null;
    let adopted = false;
    const recoveringInitialPhoto = photoRestoreError;
    setLoading(true);
    try {
      stored = await prepareAndStoreLocalPhoto(file, {
        onDiagnostic: (diagnostic) => console.debug('[WALLD photo]', { ...diagnostic, platform: telegram.platform() }),
        onSourceReadDiagnostic: (diagnostic) => {
          console.debug('[WALLD photo]', { ...diagnostic, platform: telegram.platform() });
          if (diagnostic.outcome === 'retry_succeeded') onPhotoReadRetrySuccess?.(diagnostic);
        },
      });
      if (operation !== uiOperation.current) { await deleteLocalPhoto(stored.id).catch(() => undefined); return; }
      const prepared = await loadLocalPhoto(stored.id);
      await engine.replacePhoto(prepared, stored.id);
      if (operation === uiOperation.current) {
        photoIdRef.current = stored.id;
        onPhotoChange(stored);
        adopted = true;
        setPhotoRestoreError(false);
        if (recoveringInitialPhoto) {
          if (setup.current.initialState) await engine.restoreState(setup.current.initialState);
          else await engine.addLayer({
            designId: setup.current.initialDesign.id,
            name: setup.current.initialDesign.name,
            sourceUrl: setup.current.initialDesign.asset,
            opacity: setup.current.initialDesign.recommendedOpacity,
            blendMode: setup.current.initialDesign.defaultBlend,
            whiteThreshold: setup.current.initialDesign.whiteRemovalThreshold,
          });
          onReadyRef.current(engine);
        }
        setTab('adjust');
      }
    }
    catch (cause) {
      if (stored && !adopted) await deleteLocalPhoto(stored.id).catch(() => undefined);
      if (operation === uiOperation.current && (!(cause instanceof Error) || cause.name !== 'AbortError')) {
        if (!adopted) onPrepareErrorRef.current(cause, file);
        if (adopted) setError(t('design.loadError'));
        else setError(t(imageUploadErrorKey(cause), { size: MAX_UPLOAD_MEGABYTES }));
      }
    }
    finally {
      if (operation === uiOperation.current) {
        setLoading(false);
        setPendingPhoto(null);
        if (photoInput.current) photoInput.current.value = '';
      }
    }
  };
  const cancelPhotoChange = () => {
    setPendingPhoto(null);
    if (photoInput.current) photoInput.current.value = '';
  };

  const selectTab = (next: EditorTab) => {
    if (next === 'pro' && !advanced.isEnabled) { setLockedNotice(advanced.isLocked); telegram.haptic('selection'); return; }
    setLockedNotice(false);
    setTab(next);
    setSheetExpanded(next === 'layers' || next === 'pro');
    engine?.setMaskMode('off');
    telegram.haptic('selection');
  };
  useEffect(() => {
    if (tab !== 'pro' || advanced.isEnabled) return;
    setTab('adjust');
    setSheetExpanded(false);
  }, [advanced.isEnabled, tab]);
  const openPicker = (mode: 'add' | 'replace') => { engine?.resetInteractionMode(); setPicker(mode); };
  const panelProps = engine && snapshot ? { engine, snapshot, onChooseDesign: openPicker, onChangePhoto: () => { engine.resetInteractionMode(); photoInput.current?.click(); } } : null;
  const editorTabs = ([
    ['tattoo', Image, t('editor.tabTattoo')], ['adjust', SlidersHorizontal, t('editor.tabAdjust')], ['layers', Layers3, t('editor.tabLayers')],
    ...(!advanced.isHidden ? [['pro', Sparkles, `${t('editor.tabAdvanced')}${advanced.isLocked ? ' ★' : ''}`] as const] : []),
  ] as const);

  return (
    <main className={`screen editor-screen${focusMode ? ' is-focus' : ''}`}>
      <header className="editor-header">
        <span>{!telegram.isAvailable() && <button className="icon-button" onClick={onBack} aria-label={t('editor.back')}><ArrowLeft /></button>}</span>
        <div className="editor-header__title"><strong>{t('editor.title')}</strong><span>{t('progress.step', { step: 2 })}</span><i className="editor-header__progress" aria-hidden="true"><b /><b /><b /></i></div>
        <div className="editor-history">
          <button className="icon-button" onClick={() => void engine?.undo()} disabled={!snapshot?.canUndo} aria-label={t('editor.undo')}><Undo2 /></button>
          <button className="icon-button" onClick={() => void engine?.redo()} disabled={!snapshot?.canRedo} aria-label={t('editor.redo')}><Redo2 /></button>
        </div>
      </header>
      <div className="editor-layout">
        <section className="canvas-stage" aria-label={t('editor.canvas')}>
          <button className="canvas-focus-button" onClick={() => setFocusMode((value) => !value)}>{focusMode ? <PanelBottom /> : <Maximize2 />}<span>{t(focusMode ? 'editor.showTools' : 'editor.focus')}</span></button>
          <canvas ref={canvasRef} width="900" height="900" tabIndex={0} aria-label={t('editor.moveHint')} />
          <div className="brush-cursor" aria-hidden="true" />
          {loading && <div className="canvas-state" role="status"><span className="spinner" />{t('editor.preparing')}</div>}
          {photoRestoreError && <div className="canvas-error canvas-error--photo" role="alert"><p>{t('editor.photoRestoreError')}</p><div><button onClick={() => setRestoreAttempt((value) => value + 1)}>{t('common.retry')}</button><button onClick={() => photoInput.current?.click()}>{t('home.chooseAnotherPhoto')}</button></div></div>}
          {!photoRestoreError && error && <div className="canvas-error" role="alert"><p>{error}</p><button onClick={() => setError('')}>{t('editor.dismiss')}</button></div>}
        </section>
        <aside className={`editor-sheet${sheetExpanded ? ' is-expanded' : ' is-compact'}`} aria-label={t('editor.controls')} aria-hidden={focusMode}>
          <button className="editor-sheet__toggle" onClick={() => setSheetExpanded((value) => !value)} aria-expanded={sheetExpanded} aria-controls="editor-tool-panel">
            <span className="editor-sheet__handle" aria-hidden="true" />
            <span>{t(sheetExpanded ? 'editor.compact' : 'editor.expand')}</span>
            {sheetExpanded ? <ChevronDown /> : <ChevronUp />}
          </button>
          <div className="editor-sheet__body" id="editor-tool-panel">
            {lockedNotice ? <LockedFeature title={t('editor.tabAdvanced')} compact /> : <>
              {panelProps && tab === 'tattoo' && <TattooPanel {...panelProps} />}
              {panelProps && tab === 'adjust' && <AdjustPanel {...panelProps} />}
              {panelProps && tab === 'layers' && <LayersPanel {...panelProps} />}
              {panelProps && tab === 'pro' && advanced.isEnabled && <ProPanel {...panelProps} />}
            </>}
          </div>
          <nav className="editor-tabs" aria-label={t('editor.tools')} style={{ gridTemplateColumns: `repeat(${editorTabs.length}, minmax(0, 1fr))` }}>
            {editorTabs.map(([id, Icon, label]) => <button key={id} className={tab === id ? 'is-active' : ''} aria-current={tab === id ? 'page' : undefined} aria-disabled={id === 'pro' && advanced.isLocked || undefined} onClick={() => selectTab(id)}><Icon /><span>{label}</span></button>)}
          </nav>
        </aside>
      </div>
      <div className="editor-finish">
        {import.meta.env.DEV && snapshot && <output className="history-debug" title={t('editor.historyTitle')}>{t('editor.historyMetric', { current: snapshot.history.index + 1, total: snapshot.history.length, size: Math.round((snapshot.history.estimatedBytes || 0) / 1024) })}</output>}
        <Button block icon={<Check />} onClick={() => {
          if (!engine || !snapshot) return;
          const selectedLayer = snapshot.state.layers.find((layer) => layer.id === snapshot.state.selectedLayerId) || snapshot.state.layers.at(-1);
          const selectedDesign = selectedLayer ? designCatalogRef.current.get(selectedLayer.designId) : undefined;
          if (selectedDesign) { engine.resetInteractionMode(); onResult(engine, selectedDesign); }
          else setError(t('editor.addError'));
        }} disabled={loading || !snapshot?.hasPhoto || !snapshot.hasLayers}>{t('editor.seeResult')}</Button>
      </div>
      <input ref={photoInput} className="visually-hidden" type="file" accept="image/*,.heic,.heif" onChange={(event) => setPendingPhoto(event.target.files?.[0] ?? null)} />
      <ConfirmDialog
        open={Boolean(pendingPhoto)}
        title={t('editor.photoTitle')}
        copy={t('editor.photoConfirm')}
        confirmLabel={t('editor.changePhoto')}
        cancelLabel={t('common.cancel')}
        busy={loading}
        danger={false}
        onCancel={cancelPhotoChange}
        onConfirm={() => void applyPhoto()}
      />
      {picker && <div ref={pickerRef} className="sheet-modal" role="dialog" aria-modal="true" aria-label={t(picker === 'add' ? 'editor.addDesign' : 'editor.replaceDesign')}>
        <button className="sheet-modal__scrim" tabIndex={-1} aria-label={t('editor.closeLibrary')} onClick={() => setPicker(null)} />
        <section className="sheet-modal__card">
          <div className="sheet-modal__handle" aria-hidden="true" />
          <header className="sheet-modal__header"><h2>{t(picker === 'add' ? 'editor.addDesign' : 'editor.replaceDesign')}</h2><button data-modal-close className="icon-button sheet-modal__close" onClick={() => setPicker(null)} aria-label={t('editor.closeLibrary')}><X /></button></header>
          <div className="sheet-modal__scroll auto-hide-scroll" {...pickerScroll}><DesignLibrary compact designs={designs} selectedId={snapshot?.state.layers.find((layer) => layer.id === snapshot.state.selectedLayerId)?.designId} onBack={() => setPicker(null)} onSelect={chooseDesign} onCustom={chooseCustom} /></div>
        </section>
      </div>}
    </main>
  );
}
